import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Pencil, FlaskConical, ExternalLink, Server, ChevronDown, Sparkles, Wand2, ListChecks } from "lucide-react";

const EMPTY_LAB = {
  title: "",
  description: "",
  instructions: "",
  environment: "docker",
  difficulty: "easy",
  estimated_time_minutes: 30,
  category: "",
  tags: [],
  thumbnail: "",
  is_published: false,
  // Environment configuration
  execution_mode: "docker",
  base_image: "",
  exposed_ports: [],
  startup_command: "",
  memory_limit: "512m",
  cpu_limit: "0.5",
  steps: [],
  // SSH (VM externe)
  ssh_host: "",
  ssh_port: 22,
  ssh_user: "",
  ssh_password: "",
  ssh_private_key: "",
};

const EMPTY_STEP = { title: "", content: "", step_type: "task", validation_goal: "", hint: "", quiz_options: ["", "", "", ""], quiz_correct_answer: 0 };

const LabsPanel = () => {
  const { token } = useAuth();
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(EMPTY_LAB);
  const [tagsInput, setTagsInput] = useState("");
  const [portsInput, setPortsInput] = useState("");
  const [showEnvConfig, setShowEnvConfig] = useState(false);
  const [genSteps, setGenSteps] = useState(false);
  const [numSteps, setNumSteps] = useState(6);
  const [testingSSH, setTestingSSH] = useState(false);

  const testSSH = async () => {
    if (!editing.ssh_host || !editing.ssh_user) {
      toast.error("Hôte et utilisateur requis.");
      return;
    }
    setTestingSSH(true);
    try {
      const res = await axios.post(
        `${API}/admin/labs/ssh-test`,
        {
          ssh_host: editing.ssh_host,
          ssh_port: editing.ssh_port || 22,
          ssh_user: editing.ssh_user,
          ssh_password: editing.ssh_password || null,
          ssh_private_key: editing.ssh_private_key || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.ok) toast.success(res.data.message || "Connexion réussie");
      else toast.error(res.data.message || "Échec de la connexion");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du test SSH");
    } finally {
      setTestingSSH(false);
    }
  };

  const steps = editing.steps || [];
  const setSteps = (updater) =>
    setEditing((prev) => ({ ...prev, steps: typeof updater === "function" ? updater(prev.steps || []) : updater }));
  const updateStep = (idx, field, value) => setSteps((s) => s.map((st, i) => (i === idx ? { ...st, [field]: value } : st)));
  const addStep = () => setSteps((s) => [...s, { ...EMPTY_STEP }]);
  const removeStep = (idx) => setSteps((s) => s.filter((_, i) => i !== idx));

  const generateSteps = async () => {
    if (!editing.title) {
      toast.error("Renseignez d'abord le titre du lab.");
      return;
    }
    setGenSteps(true);
    try {
      const res = await axios.post(
        `${API}/admin/ai/lab-steps`,
        { title: editing.title, description: editing.description, environment: editing.environment, difficulty: editing.difficulty, num_steps: numSteps, language: "fr" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSteps(res.data.steps || []);
      toast.success(`${res.data.count} étape(s) générée(s) par l'IA`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la génération IA");
    } finally {
      setGenSteps(false);
    }
  };

  const fetchLabs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/labs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabs(res.data.labs || []);
    } catch {
      toast.error("Erreur lors du chargement des labos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(EMPTY_LAB);
    setTagsInput("");
    setPortsInput("");
    setShowEnvConfig(false);
    setShowDialog(true);
  };

  const openEdit = (lab) => {
    setEditing({ ...EMPTY_LAB, ...lab });
    setTagsInput((lab.tags || []).join(", "));
    setPortsInput((lab.exposed_ports || []).join(", "));
    setShowEnvConfig(false);
    setShowDialog(true);
  };

  const saveLab = async () => {
    if (!editing.title || !editing.description || !editing.instructions) {
      toast.error("Titre, description et instructions requis");
      return;
    }
    setSaving(true);
    const payload = {
      ...editing,
      tags: tagsInput.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
      exposed_ports: portsInput.split(",").map((p) => parseInt(p.trim())).filter((n) => !isNaN(n)),
      estimated_time_minutes: parseInt(editing.estimated_time_minutes) || 30,
      base_image: editing.base_image?.trim() || null,
      startup_command: editing.startup_command?.trim() || null,
    };
    try {
      if (editing.lab_id) {
        await axios.put(`${API}/labs/${editing.lab_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Lab mis à jour");
      } else {
        await axios.post(`${API}/labs`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Lab créé");
      }
      setShowDialog(false);
      fetchLabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (lab) => {
    try {
      await axios.put(
        `${API}/labs/${lab.lab_id}`,
        { is_published: !lab.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLabs();
    } catch {
      toast.error("Erreur");
    }
  };

  const deleteLab = async (lab) => {
    if (!window.confirm(`Supprimer le lab "${lab.title}" ?`)) return;
    try {
      await axios.delete(`${API}/labs/${lab.lab_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lab supprimé");
      fetchLabs();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div data-testid="labs-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-6 h-6 text-primary" />
          <div>
            <h2 className="font-heading font-bold text-xl">Labos interactifs</h2>
            <p className="text-sm text-muted-foreground">Créez, configurez l'environnement et publiez des labos autonomes</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="new-lab-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau lab
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : labs.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
          <FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Aucun lab. Créez votre premier lab autonome.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl divide-y divide-border">
          {labs.map((lab) => (
            <div key={lab.lab_id} className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium truncate">{lab.title}</span>
                  <Badge variant="outline" className="uppercase text-xs">{lab.environment}</Badge>
                  <Badge variant="outline" className="text-xs">{lab.execution_mode || "docker"}</Badge>
                  {lab.course_id && <Badge variant="outline" className="text-xs">rattaché cours</Badge>}
                </div>
                <p className="text-sm text-muted-foreground truncate">{lab.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Publié</span>
                <Switch checked={!!lab.is_published} onCheckedChange={() => togglePublish(lab)} />
              </div>
              <Link to={`/labs/${lab.lab_id}`} target="_blank">
                <Button variant="ghost" size="icon"><ExternalLink className="w-4 h-4" /></Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={() => openEdit(lab)}><Pencil className="w-4 h-4" /></Button>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteLab(lab)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Dialog create/edit */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing.lab_id ? "Modifier le lab" : "Nouveau lab"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Titre *</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Créer votre premier conteneur" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Ce que l'apprenant va réaliser..." rows={2} />
            </div>
            <div>
              <Label>Instructions (Markdown supporté) *</Label>
              <Textarea value={editing.instructions} onChange={(e) => setEditing({ ...editing, instructions: e.target.value })} placeholder={"1. Exécutez la commande...\n2. Vérifiez que..."} rows={5} className="font-mono text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Environnement</Label>
                <Select value={editing.environment} onValueChange={(v) => setEditing({ ...editing, environment: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="docker">Docker</SelectItem>
                    <SelectItem value="kubernetes">Kubernetes</SelectItem>
                    <SelectItem value="terraform">Terraform</SelectItem>
                    <SelectItem value="ansible">Ansible</SelectItem>
                    <SelectItem value="linux">Linux</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulté</Label>
                <Select value={editing.difficulty} onValueChange={(v) => setEditing({ ...editing, difficulty: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Facile</SelectItem>
                    <SelectItem value="medium">Intermédiaire</SelectItem>
                    <SelectItem value="hard">Avancé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Temps estimé (min)</Label>
                <Input type="number" min={1} value={editing.estimated_time_minutes} onChange={(e) => setEditing({ ...editing, estimated_time_minutes: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tags (séparés par des virgules)</Label>
                <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="docker, conteneurs, débutant" />
              </div>
              <div>
                <Label>Image de couverture (URL, optionnel)</Label>
                <Input value={editing.thumbnail || ""} onChange={(e) => setEditing({ ...editing, thumbnail: e.target.value })} placeholder="/api/uploads/..." />
              </div>
            </div>

            {/* Configuration de l'environnement (Phase 2) */}
            <div className="border border-border rounded-lg">
              <button
                type="button"
                onClick={() => setShowEnvConfig(!showEnvConfig)}
                className="w-full flex items-center justify-between p-3 text-left"
              >
                <span className="flex items-center gap-2 font-medium">
                  <Server className="w-4 h-4 text-primary" /> Configuration de l'environnement
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showEnvConfig ? "rotate-180" : ""}`} />
              </button>
              {showEnvConfig && (
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Mode d'exécution</Label>
                      <Select value={editing.execution_mode} onValueChange={(v) => setEditing({ ...editing, execution_mode: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="docker">Conteneur réel (Docker)</SelectItem>
                          <SelectItem value="ssh">VM externe (SSH)</SelectItem>
                          <SelectItem value="simulation">Simulation</SelectItem>
                          <SelectItem value="external">Service externe</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {editing.execution_mode !== "ssh" && (
                      <div>
                        <Label>Image de base (optionnel)</Label>
                        <Input value={editing.base_image || ""} onChange={(e) => setEditing({ ...editing, base_image: e.target.value })} placeholder="ubuntu:22.04, docker:24-dind…" />
                      </div>
                    )}
                  </div>

                  {/* Indication k3s : cluster K8s auto */}
                  {editing.environment === "kubernetes" && editing.execution_mode === "docker" && (
                    <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm text-primary flex items-start gap-2">
                      <Server className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>Un <strong>vrai cluster Kubernetes (k3s)</strong> sera démarré automatiquement pour cet environnement — <code>kubectl</code> fonctionne directement, sans configuration.</span>
                    </div>
                  )}
                  {editing.environment === "kubernetes" && editing.execution_mode === "ssh" && (
                    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-300">
                      💡 Pour un cluster K8s <strong>sans VM externe</strong>, choisissez le mode <strong>« Conteneur réel (Docker) »</strong> : un k3s self-contained démarrera automatiquement.
                    </div>
                  )}

                  {editing.execution_mode === "ssh" ? (
                    // --- Config SSH (VM externe) ---
                    <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
                      <p className="text-xs text-muted-foreground">
                        Le terminal de l'étudiant s'exécute sur cette VM via SSH. <strong>L'étudiant ne voit rien de la connexion.</strong> Idéal pour un vrai cluster (ex : VM avec <strong>minikube</strong> préinstallé).
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <Label>Hôte / IP</Label>
                          <Input value={editing.ssh_host || ""} onChange={(e) => setEditing({ ...editing, ssh_host: e.target.value })} placeholder="192.168.1.50" />
                        </div>
                        <div>
                          <Label>Port</Label>
                          <Input type="number" value={editing.ssh_port || 22} onChange={(e) => setEditing({ ...editing, ssh_port: parseInt(e.target.value) || 22 })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Utilisateur</Label>
                          <Input value={editing.ssh_user || ""} onChange={(e) => setEditing({ ...editing, ssh_user: e.target.value })} placeholder="ubuntu" />
                        </div>
                        <div>
                          <Label>Mot de passe</Label>
                          <Input type="password" value={editing.ssh_password || ""} onChange={(e) => setEditing({ ...editing, ssh_password: e.target.value })} placeholder="••••••" />
                        </div>
                      </div>
                      <div>
                        <Label>Clé privée SSH (optionnel, à la place du mot de passe)</Label>
                        <Textarea value={editing.ssh_private_key || ""} onChange={(e) => setEditing({ ...editing, ssh_private_key: e.target.value })} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" rows={3} className="font-mono text-xs" />
                      </div>
                      <Button type="button" size="sm" variant="outline" onClick={testSSH} disabled={testingSSH}>
                        {testingSSH ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Server className="w-4 h-4 mr-1" />} Tester la connexion
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Mémoire</Label>
                          <Input value={editing.memory_limit} onChange={(e) => setEditing({ ...editing, memory_limit: e.target.value })} placeholder="512m" />
                        </div>
                        <div>
                          <Label>CPU</Label>
                          <Input value={editing.cpu_limit} onChange={(e) => setEditing({ ...editing, cpu_limit: e.target.value })} placeholder="0.5" />
                        </div>
                      </div>
                      <div>
                        <Label>Ports web à exposer (preview, séparés par des virgules)</Label>
                        <Input value={portsInput} onChange={(e) => setPortsInput(e.target.value)} placeholder="8080, 3000" />
                      </div>
                      <div>
                        <Label>Commande de démarrage (optionnel)</Label>
                        <Input value={editing.startup_command || ""} onChange={(e) => setEditing({ ...editing, startup_command: e.target.value })} placeholder="apt-get update && ..." className="font-mono text-sm" />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Étapes guidées */}
            <div className="border border-border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="flex items-center gap-2 font-medium"><ListChecks className="w-4 h-4 text-primary" /> Étapes guidées ({steps.length})</span>
                <div className="flex items-center gap-2">
                  <Input type="number" min={2} max={15} value={numSteps} onChange={(e) => setNumSteps(parseInt(e.target.value) || 6)} className="w-16 h-8" />
                  <Button type="button" size="sm" variant="outline" onClick={generateSteps} disabled={genSteps}>
                    {genSteps ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Générer (IA)
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={addStep}><Plus className="w-4 h-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Type <strong>task</strong> = l'IA vérifie les commandes tapées pour valider. <strong>quiz</strong> = QCM. <strong>instruction</strong> = théorie.
              </p>

              <div className="space-y-3">
                {steps.map((st, idx) => (
                  <div key={idx} className="rounded-lg border border-[#222] bg-muted/20 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground shrink-0">#{idx + 1}</span>
                      <Input value={st.title} onChange={(e) => updateStep(idx, "title", e.target.value)} placeholder="Titre de l'étape" className="h-8" />
                      <Select value={st.step_type} onValueChange={(v) => updateStep(idx, "step_type", v)}>
                        <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="instruction">Instruction</SelectItem>
                          <SelectItem value="task">Tâche (IA)</SelectItem>
                          <SelectItem value="quiz">Quiz</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeStep(idx)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <Textarea value={st.content} onChange={(e) => updateStep(idx, "content", e.target.value)} placeholder="Contenu (Markdown : théorie + instructions, ``` pour le code)" rows={3} className="text-sm" />
                    {st.step_type === "task" && (
                      <Textarea value={st.validation_goal || ""} onChange={(e) => updateStep(idx, "validation_goal", e.target.value)} placeholder="Objectif à vérifier par l'IA (ex: 'un conteneur nginx nommé web est en cours d'exécution')" rows={2} className="text-sm" />
                    )}
                    {st.step_type === "quiz" && (
                      <div className="space-y-1">
                        {(st.quiz_options || ["", "", "", ""]).map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <button type="button" onClick={() => updateStep(idx, "quiz_correct_answer", oi)} className={`w-5 h-5 rounded-full border-2 shrink-0 ${st.quiz_correct_answer === oi ? "border-primary bg-primary" : "border-muted-foreground"}`} />
                            <Input value={opt} onChange={(e) => { const opts = [...(st.quiz_options || ["", "", "", ""])]; opts[oi] = e.target.value; updateStep(idx, "quiz_options", opts); }} placeholder={`Option ${oi + 1}`} className="h-8" />
                          </div>
                        ))}
                      </div>
                    )}
                    <Input value={st.hint || ""} onChange={(e) => updateStep(idx, "hint", e.target.value)} placeholder="Indice (optionnel)" className="h-8 text-sm" />
                  </div>
                ))}
                {steps.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-3">Aucune étape. Générez-les avec l'IA ou ajoutez-les manuellement.</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Switch checked={!!editing.is_published} onCheckedChange={(v) => setEditing({ ...editing, is_published: v })} />
              <Label>Publier ce lab (visible dans le catalogue)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annuler</Button>
            <Button onClick={saveLab} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing.lab_id ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabsPanel;
